/**
 * 日本語を言葉のまとまりで折る。
 *
 * BudouX (budoux@0.6.4、Apache License 2.0) の Parser を写したもので、文を文節に切り、
 * 切れ目に <wbr> を入れる。<wbr> は iOS の WebKit でも効く (CSS の word-break: auto-phrase は Chrome だけ)。
 * 置く側は word-break: keep-all を当て、<wbr> の所でだけ折れるようにする (.jd の中は app.css が当てる)。
 *
 * npm の budoux をそのまま入れないのは、入口 (index.js) が jsdom を読みに行き、依存に jsdom と commander が付くため。
 * 使うのは文を切る関数と日本語の model だけなので、その 2 つを写した。
 *
 * 描画の中で切るので、書き出しの HTML (lab-astro) と、そのあとの hydrate で同じ形になる。
 */
import { Children, Fragment, isValidElement, type ReactNode } from "react";
import { jaModel } from "./budoux-ja-model";

type Model = Map<string, Map<string, number>>;

const MODEL: Model = new Map(
  Object.entries(jaModel).map(([k, v]) => [k, new Map(Object.entries(v))]),
);

const BASE_SCORE =
  -0.5 *
  [...MODEL.values()].flatMap((group) => [...group.values()]).reduce((a, b) => a + b, 0);

const FEATURES: [string, number, number][] = [
  ["UW1", -3, -2],
  ["UW2", -2, -1],
  ["UW3", -1, 0],
  ["UW4", 0, 1],
  ["UW5", 1, 2],
  ["UW6", 2, 3],
  ["BW1", -2, 0],
  ["BW2", -1, 1],
  ["BW3", 0, 2],
  ["TW1", -3, 0],
  ["TW2", -2, 1],
  ["TW3", -1, 2],
  ["TW4", 0, 3],
];

/** budoux の Parser.parseBoundaries と同じ計算。substring の範囲の外は空文字になるのも同じ */
function boundaries(sentence: string): number[] {
  const out: number[] = [];
  for (let i = 1; i < sentence.length; i++) {
    let score = BASE_SCORE;
    for (const [key, from, to] of FEATURES) {
      score += MODEL.get(key)?.get(sentence.substring(i + from, i + to)) || 0;
    }
    if (score > 0) out.push(i);
  }
  return out;
}

/** 行の最後に 1 つだけ残っても読める長さ。これより短い末尾は前の塊とつなげる */
const MIN_TAIL = 5;

/**
 * 文を文節に切る。
 *
 * 末尾の塊が短いと、行の最後に「ます。」だけが落ちる。末尾から MIN_TAIL 字に届くまで前の塊とつなげ、
 * 最後の行に 3 字以下が残らないようにする。
 */
export function phrases(sentence: string): string[] {
  if (sentence === "") return [];
  const chunks: string[] = [];
  let start = 0;
  for (const b of boundaries(sentence)) {
    chunks.push(sentence.slice(start, b));
    start = b;
  }
  chunks.push(sentence.slice(start));
  // 縦棒 (｜ |) が行の頭に来ると、題が「｜時間のつくり方と、」と棒で始まる行になる。
  // 棒は前の塊の末尾に付け、棒の直後を切れ目にする (折れるのは棒のあとだけ)
  for (let i = 0; i < chunks.length; i++) {
    const m = /^[｜|]+/.exec(chunks[i]);
    if (m && i > 0) {
      chunks[i - 1] += m[0];
      chunks[i] = chunks[i].slice(m[0].length);
      if (chunks[i] === "") chunks.splice(i--, 1);
    }
    const inner = /[｜|]+(?=[^｜|])/.exec(chunks[i] ?? "");
    if (inner) {
      const cut = inner.index + inner[0].length;
      chunks.splice(i, 1, chunks[i].slice(0, cut), chunks[i].slice(cut));
    }
  }
  // 「60」と「秒」のように、数だけの塊は次の塊とつなげる。数と単位が別の行に分かれない
  for (let i = chunks.length - 2; i >= 0; i--) {
    if (/^[0-9０-９.,]+$/.test(chunks[i])) chunks.splice(i, 2, chunks[i] + chunks[i + 1]);
  }
  // 「と / いう」「と / いった」「と / して」は model が切るが、読む側には 1 語。前の塊とつなげる (「アジャイルと / いう考え方」「階層と / して」)
  for (let i = chunks.length - 1; i > 0; i--) {
    if (/と$/.test(chunks[i - 1]) && /^(い(う|っ)|して)/.test(chunks[i])) chunks.splice(i - 1, 2, chunks[i - 1] + chunks[i]);
  }
  // 漢字で終わる塊のあとの「漢字 1 字 + かな」も、熟語の途中で切ったもの (「一方 / 向に」)。前の塊とつなげる
  for (let i = chunks.length - 1; i > 0; i--) {
    if (/[一-龠]$/.test(chunks[i - 1]) && /^[一-龠][ぁ-ん]/.test(chunks[i])) chunks.splice(i - 1, 2, chunks[i - 1] + chunks[i]);
  }
  // 1 字だけの塊は、model が語の途中で切ったもの (「感|想から」)。次の塊とつなげる
  for (let i = chunks.length - 2; i >= 0; i--) {
    if ([...chunks[i]].length === 1) chunks.splice(i, 2, chunks[i] + chunks[i + 1]);
  }
  while (chunks.length > 1 && [...chunks[chunks.length - 1]].length < MIN_TAIL) {
    const tail = chunks.pop()!;
    chunks[chunks.length - 1] += tail;
  }
  return chunks;
}

/**
 * 文節の切れ目に幅の無い空白 (U+200B) を入れた 1 本の文字列。word-break: keep-all と組むと、Chrome も Safari も文節の切れ目でだけ折る。
 * 数と単位 (「30」と「日」、「30 日」の空白) は折れ目にしない
 */
export function joinPhrases(sentence: string): string {
  return phrases(sentence)
    .join("\u200b")
    .replace(/([0-9,.])\u200b/g, "$1")
    .replace(/([0-9,]) (?=[日回問円件本個人字分時月年種])/g, "$1\u00a0");
}

/**
 * 棒の前に WORD JOINER (U+2060、幅も字形も無い) を置く。かなや漢字と棒のあいだは
 * word-break: keep-all でも折れるので、これが無いと「学ぶ」と「｜」のあいだで行が替わる
 */
export function noBreakBeforeBar(text: string): string {
  return text.replace(/(?<=[^｜|])(?=[｜|])/g, "\u2060");
}

/** 文字列を、文節の切れ目に <wbr> を入れた形にする */
function withBreaks(text: string, key: string): ReactNode {
  const parts = phrases(text);
  if (parts.length < 2) return text;
  return (
    <Fragment key={key}>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {noBreakBeforeBar(p)}
        </Fragment>
      ))}
    </Fragment>
  );
}

/**
 * 子の文字列に <wbr> を入れる。要素の子はそのまま通す (中まで入らない)。
 * 要素をまたいで 1 つの文として切ることはしない。印 (.jd-mk) のように文の途中に要素を置くときは、
 * 要素の前後の文字列がそれぞれ文節で切られる。
 */
export function Bx({ children }: { children: ReactNode }) {
  return (
    <>
      {Children.map(children, (child, i) => {
        if (typeof child === "string") return withBreaks(child, `s${i}`);
        if (typeof child === "number") return child;
        if (isValidElement(child)) return child;
        return child;
      })}
    </>
  );
}

const HTML_ENTITY = /(&[#a-zA-Z0-9]+;)/;
/** 数と単位 (「1 つ」「30 分」)。空白の所で割れないよう 1 つの塊にする (MarkdownContent の rehypeKeepNumberUnits と同じ単位の並び) */
const HTML_NUMBER_UNIT = /(第?[0-9０-９]+(?:[.,][0-9]+)? ?(?:項目|ページ|章|節|部|回|本|問|分|秒|時間|日|週|か月|ヶ月|カ月|月|年|個|件|人|行|字|枚|段|歩|番|点|つ|万円|円|KB|MB|GB|%))/;
/** 中の字に切れ目を入れない要素 (コード、整形済み、スクリプト) */
const HTML_SKIP = /^<(code|pre|kbd|samp|script|style)\b/i;

/**
 * 書き出した HTML の字の所に、文節の切れ目の <wbr> を入れる (用語ページの本文のように MarkdownContent を通らない経路)。
 * タグの中と、コードと整形済みの中は触らない。文字参照 (&amp; など) は割らない。
 * 置く側は word-break: keep-all を当て、<wbr> と空白の所でだけ折れるようにする
 */
export function phraseHtml(html: string): string {
  let skipDepth = 0;
  return html
    .split(/(<[^>]+>)/)
    .map((part) => {
      if (part.startsWith("<")) {
        if (HTML_SKIP.test(part)) skipDepth += 1;
        else if (/^<\/(code|pre|kbd|samp|script|style)\b/i.test(part)) skipDepth = Math.max(0, skipDepth - 1);
        return part;
      }
      if (skipDepth > 0 || part.trim() === "") return part;
      return part
        .split(HTML_ENTITY)
        .map((t, i) =>
          i % 2 === 1
            ? t
            : t
                .split(HTML_NUMBER_UNIT)
                .map((u, j) => (j % 2 === 1 ? `<span class="whitespace-nowrap">${u}</span>` : phrases(u).map(noBreakBeforeBar).join("<wbr>")))
                .join(""),
        )
        .join("");
    })
    .join("");
}
